import { useEffect, useState, useRef, useCallback } from 'react';
import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { shapesStore, connectorsStore } from '../collections/diagramCollections';
import { base64ToUint8Array } from '../utils/yjs';
import { useAuthenticatedFetch } from './useAuth';
import { notifyShapeChange } from './useDiagramShapes';
import { notifyConnectorChange } from './useDiagramConnectors';
import type { Shape, Connector } from '../schemas/diagramSchemas';
import { useDiagramStore } from '../stores/diagramStore';
import { Logger } from '../utils/logger';
import { readYjsValue } from '../lib/diagram/yjsCompat';

import { getStableTabClientId } from '../utils/stableClientId';
import { getCookie } from '../lib/cookies';

interface UseDiagramOptions {
  diagramId: string;
  userId: string;
  userName?: string;
  isLinkMode?: boolean; // New prop to determine if we should use link endpoints
}

/**
 * Hybrid Yjs CRDT + TanStack DB hook with Real-Time Awareness
 * Supports both authenticated and link-based access
 * 
 * Architecture:
 * - Yjs Document: Source of truth for writes (CRDT conflict resolution)
 * - Awareness: Real-time cursor/awareness via WebSocket
 * - Aggressive save: 20ms debounce OR 3 updates (optimized for quick refresh)
 * - Synchronous beforeunload: Uses navigator.sendBeacon for 100% reliability
 * - Electric sync: Polls for server changes every 5 seconds (recovery)
 * - WebSocket: Broadcasts Yjs updates + awareness in real-time
 */
// Global instances to prevent StrictMode duplication
let globalYdoc: Y.Doc | null = null;
let globalAwareness: awarenessProtocol.Awareness | null = null;

export function useDiagramWithTanstackDBLink(options: UseDiagramOptions) {
  const { diagramId, userId, userName, isLinkMode = false } = options;
  const authenticatedFetch = useAuthenticatedFetch();

  // Create stable Yjs document + awareness (singleton pattern for StrictMode)
  // Use stable tab-level clientId that persists across refreshes
  const [ydoc] = useState(() => {
    if (!globalYdoc) {
      const doc = new Y.Doc();
      doc.clientID = getStableTabClientId(diagramId);
      globalYdoc = doc;
    }
    return globalYdoc;
  });

  const [awareness] = useState(() => {
    if (!globalAwareness) {
      globalAwareness = new awarenessProtocol.Awareness(ydoc);
    }
    return globalAwareness;
  });

  const [isConnected, setIsConnected] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Track pending updates for batching
  const pendingUpdatesRef = useRef<Uint8Array[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const documentEpochRef = useRef(0);

  // WebSocket connection for real-time awareness + Yjs updates
  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;

    const connectWebSocket = () => {
      if (cancelled) return;

      const token = getCookie('gravity_token') ?? undefined;

      // In development, connect directly to Wrangler to avoid Vite proxy issues
      // Check if we're in dev mode by looking at the port
      const isDev = window.location.port === '5174';
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

      // In dev, use direct connection to Wrangler; in prod use same host
      const wsHost = isDev ? 'localhost:8787' : window.location.host;
      
      // Use different WebSocket endpoints based on access mode
      const wsPath = isLinkMode 
        ? `/api/link/diagrams/${diagramId}/awareness/ws`
        : `/api/diagrams/${diagramId}/awareness/ws`;
      
      const wsUrl = `${protocol}//${wsHost}${wsPath}${token ? `?token=${token}` : ''}`;

      Logger.debug('DiagramTanstackLink', 'WebSocket URL:', wsUrl, { isDev, protocol, wsHost, isLinkMode });
      
      Logger.debug('DiagramTanstackLink', '🔌 Connecting WebSocket for awareness + Yjs sync...');
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        if (cancelled) {
          return;
        }

        Logger.debug('DiagramTanstackLink', '✅ WebSocket connected');
        setIsConnected(true);
        setError(null);

        // Delay initial awareness state to ensure connection is stable
        setTimeout(() => {
          if (ws && ws.readyState === WebSocket.OPEN && !cancelled) {
            const update = awarenessProtocol.encodeAwarenessUpdate(
              awareness,
              [awareness.clientID]
            );
            ws.send(update);
            Logger.debug('DiagramTanstackLink', '📤 Sent initial awareness state');
          }
        }, 100);
      };

      ws.onmessage = (event) => {
        if (cancelled) return;

        try {
          // Receive raw awareness updates (no type prefix)
          const data = new Uint8Array(event.data);

          if (data.length > 0) {
            // Apply as awareness update (silent - happens frequently)
            awarenessProtocol.applyAwarenessUpdate(awareness, data, 'websocket');
          }
        } catch (err) {
          console.error('[DiagramTanstackLink] Error processing WebSocket message:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('[DiagramTanstackLink] ❌ WebSocket error:', err);
        setIsConnected(false);
      };

      ws.onclose = () => {
        if (cancelled) return;
        Logger.debug('DiagramTanstackLink', '🔌 WebSocket closed, reconnecting in 2s...');
        setIsConnected(false);
        wsRef.current = null;
        
        // Reconnect after 2 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!cancelled) connectWebSocket();
        }, 2000);
      };
    };

    connectWebSocket();

    return () => {
      cancelled = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (ws) {
        // Only close if WebSocket is OPEN or CLOSING, not CONNECTING or CLOSED
        // This prevents "WebSocket is closed before connection is established" error in React Strict Mode
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
          ws.close();
        }
      }
      wsRef.current = null;
    };
  }, [diagramId, ydoc, awareness, isLinkMode]);

  // Note: Yjs updates are handled via API/Electric, not WebSocket
  // WebSocket is only used for awareness (cursors, user awareness)

  // Broadcast awareness changes via WebSocket
  useEffect(() => {
    const handleAwarenessUpdate = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => {
      // Don't broadcast updates that came from WebSocket
      if (origin === 'websocket') return;

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const changedClients = added.concat(updated).concat(removed);
        const update = awarenessProtocol.encodeAwarenessUpdate(awareness, changedClients);

        // Send raw awareness update (no type prefix)
        ws.send(update);
      }
    };

    awareness.on('update', handleAwarenessUpdate);

    return () => {
      awareness.off('update', handleAwarenessUpdate);
    };
  }, [awareness]);

  // Set initial awareness state and update when userName changes
  useEffect(() => {
    // Initialize awareness state if it's null
    if (awareness.getLocalState() === null) {
      awareness.setLocalState({});
    }
    
    // Only set awareness if we have a real userName (not default)
    const displayName = userName && userName !== 'Anonymous User'
      ? userName
      : `User ${ydoc.clientID}`;

    awareness.setLocalStateField('user', {
      id: userId,
      name: displayName,
      color: `hsl(${(ydoc.clientID * 137.5) % 360}, 70%, 50%)`,
      timestamp: Date.now(),
    });
  }, [awareness, userId, userName, ydoc.clientID]);

  // Clean up stale awareness states for the same user ID
  useEffect(() => {
    if (!userId) return;

    const cleanupStaleStates = () => {
      const states = awareness.getStates();
      const currentTime = Date.now();
      const staleThreshold = 30000; // 30 seconds

      states.forEach((state, clientId) => {
        const user = state.user as { id?: string; timestamp?: number } | undefined;
        
        // If this is the same user ID but different client ID, and it's stale
        if (user?.id === userId && clientId !== ydoc.clientID) {
          const lastSeen = user.timestamp || 0;
          if ((currentTime - lastSeen) > staleThreshold) {
            // Try to remove the stale awareness state by setting it to null
            console.log('[DiagramTanstackLink] Attempting to remove stale awareness state for user:', userId, 'client:', clientId);
            
            // Create a new awareness state without the stale client
            const newState = { ...awareness.getLocalState() };
            newState.cleanupStaleClient = {
              clientId,
              userId,
              timestamp: currentTime
            };
            awareness.setLocalState(newState);
            
            // Clear the cleanup message after a short delay
            setTimeout(() => {
              const currentState = awareness.getLocalState();
              if (currentState?.cleanupStaleClient) {
                delete currentState.cleanupStaleClient;
                awareness.setLocalState(currentState);
              }
            }, 1000);
          }
        }
      });
    };

    // Run cleanup periodically
    const cleanupInterval = setInterval(cleanupStaleStates, 15000); // Every 15 seconds

    return () => {
      clearInterval(cleanupInterval);
    };
  }, [awareness, userId, ydoc.clientID]);

  // Cleanup awareness on unmount and page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear awareness state before page unload to prevent stale states
      if (awareness.getLocalState()) {
        awareness.setLocalState(null);
        console.log('[DiagramTanstackLink] Cleared awareness state on page unload');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      awareness.destroy();
      // Clear global instances when the last instance unmounts
      globalYdoc = null;
      globalAwareness = null;
    };
  }, [awareness]);

  // Load initial snapshot
  useEffect(() => {
    let cancelled = false;

    async function loadSnapshot() {
      try {
        // Use different endpoints based on access mode
        const snapshotUrl = isLinkMode 
          ? `/api/link/diagrams/${diagramId}/snapshot`
          : `/api/diagrams/${diagramId}/snapshot`;
        
        const response = isLinkMode 
          ? await fetch(snapshotUrl, { credentials: 'include' })
          : await authenticatedFetch(snapshotUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to load snapshot: ${response.statusText}`);
        }

        const data = await response.json();
        documentEpochRef.current = Number.isFinite(Number(data.documentEpoch))
          ? Math.max(0, Number(data.documentEpoch))
          : 0;
        
        if (cancelled) return;

        if (data.snapshotBytesBase64) {
          const snapshotBytes = base64ToUint8Array(data.snapshotBytesBase64);
          // Mark origin as 'server' so our update listener will ignore it
          Y.applyUpdate(ydoc, snapshotBytes, 'server');
          
          // Debug: check what's in the doc after applying snapshot
          const shapesMap = ydoc.getMap('shapes');
          Logger.debug('DiagramTanstackLink', 'Applied initial snapshot:', {
            diagramId,
            size: snapshotBytes.length,
            shapesInDoc: shapesMap.size,
            shapeKeys: Array.from(shapesMap.keys()),
            isLinkMode,
          });
        }

        Logger.debug('DiagramTanstackLink', '🚀 Marking as READY (snapshot loaded)!');
        setIsReady(true);
      } catch (err) {
        console.error('[DiagramTanstackLink] Failed to load snapshot:', err);
        if (!cancelled) {
          setError(err as Error);
          Logger.debug('DiagramTanstackLink', '🚀 Marking as READY (error recovery)!');
          setIsReady(true); // Continue with empty doc
        }
      }
    }

    loadSnapshot();

    return () => {
      cancelled = true;
    };
  }, [diagramId, ydoc, authenticatedFetch, isLinkMode]);

  // Sync Yjs maps to in-memory stores (one-way: Yjs → Store)
  useEffect(() => {
    if (!isReady) return;

    const shapesMap = ydoc.getMap('shapes');
    const connectorsMap = ydoc.getMap('connectors');

    // Initial population from Yjs to stores
    shapesStore.clear();
    connectorsStore.clear();
    
    // Get diagramStore for UI rendering
    const diagramStore = useDiagramStore.getState();

    const mapConnectorToEdge = (connector: Connector) => {
      const props = (connector as any).props as Record<string, unknown> | undefined;
      const hasNestedAttrs = props && typeof props === 'object' && 'attrs' in props;
      const rawAttrs = hasNestedAttrs
        ? ((props as any).attrs || {})
        : (() => {
            const fromProps = (props && typeof props === 'object') ? ({ ...(props as Record<string, unknown>) }) : {};
            delete (fromProps as any).fromAnchor;
            delete (fromProps as any).toAnchor;
            delete (fromProps as any).fromOffset;
            delete (fromProps as any).toOffset;
            delete (fromProps as any).sourceAnchor;
            delete (fromProps as any).targetAnchor;
            delete (fromProps as any).sourceOffset;
            delete (fromProps as any).targetOffset;
            return Object.keys(fromProps).length > 0 ? fromProps : ((connector as any).attrs || {});
          })();
      const connectorAttrs = {
        ...rawAttrs,
        ...(((connector as any).routingType && !(rawAttrs as any)?.routingType)
          ? { routingType: (connector as any).routingType }
          : {}),
      };
      const fromId = (connector as any).sourceShapeId || (connector as any).source || (connector as any).from;
      const toId = (connector as any).targetShapeId || (connector as any).target || (connector as any).to;

      return {
        id: connector.id,
        type: connector.type,
        from: fromId,
        to: toId,
        attrs: connectorAttrs,
        fromAnchor: (connector as any).fromAnchor || (props as any)?.fromAnchor,
        toAnchor: (connector as any).toAnchor || (props as any)?.toAnchor,
        fromOffset: (connector as any).fromOffset || (props as any)?.fromOffset,
        toOffset: (connector as any).toOffset || (props as any)?.toOffset,
      } as any;
    };
    
    shapesMap.forEach((raw, key) => {
      const shapeData = readYjsValue<Record<string, unknown>>(raw);
      const shape = { id: key, ...shapeData } as Shape;
      shapesStore.set(key, shape);
      
      // Also add to Zustand diagramStore for UI rendering
      diagramStore.addNode(shape);
    });
    
    connectorsMap.forEach((raw, key) => {
      const connectorData = readYjsValue<Record<string, unknown>>(raw);
      const connector = { id: key, ...connectorData } as Connector;
      connectorsStore.set(key, connector);
      
      // Also add to Zustand diagramStore for UI rendering (edges/connectors)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      diagramStore.addEdge(mapConnectorToEdge(connector));
    });
    
    // Now that all initial shapes have been processed, enable shape limit checks
    diagramStore.setInitialLoading(false);

    // Notify React about initial data
    notifyShapeChange();
    notifyConnectorChange();

    // Observe shapes changes and sync to BOTH stores.
    // Uses observeDeep so nested Y.Map property changes (from ShapeMapAdapter
    // granular diffs) are caught, not just top-level key add/delete/replace.
    const handleShapesChangeDeep = (events: Y.YEvent<any>[]) => {
      const changedIds = new Set<string>();
      const deletedIds = new Set<string>();

      for (const event of events) {
        if (event.path.length === 0 && event instanceof Y.YMapEvent) {
          // Top-level shapes map: add/delete/replace of whole shapes
          event.changes.keys.forEach((change, key) => {
            if (change.action === 'delete') {
              deletedIds.add(key);
            } else {
              changedIds.add(key);
            }
          });
        } else if (event.path.length >= 1) {
          // Nested change inside a shape's Y.Map (e.g. x, y property diff)
          changedIds.add(String(event.path[0]));
        }
      }

      for (const key of deletedIds) {
        shapesStore.delete(key);
        useDiagramStore.getState().deleteNode(key);
      }

      for (const key of changedIds) {
        const raw = shapesMap.get(key);
        if (raw) {
          const value = readYjsValue<Record<string, unknown>>(raw);
          const shape = { id: key, ...value } as Shape;
          shapesStore.set(key, shape);
          useDiagramStore.getState().addNode(shape, true);
        }
      }

      if (changedIds.size > 0 || deletedIds.size > 0) {
        notifyShapeChange();
      }
    };

    // Observe connectors changes and sync to BOTH stores
    const handleConnectorsChange = (event: Y.YMapEvent<unknown>) => {
      event.changes.keys.forEach((change, key) => {
        if (change.action === 'add' || change.action === 'update') {
          const raw = connectorsMap.get(key);
          if (raw) {
            const value = readYjsValue<Record<string, unknown>>(raw);
            const connector = { id: key, ...value } as Connector;
            connectorsStore.set(key, connector);
            
            // Also sync to Zustand diagramStore for UI rendering
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            useDiagramStore.getState().addEdge(mapConnectorToEdge(connector), true);
          }
        } else if (change.action === 'delete') {
          connectorsStore.delete(key);
          
          // Also delete from Zustand diagramStore
          useDiagramStore.getState().deleteEdge(key);
        }
      });
      // Notify subscribers about changes
      notifyConnectorChange();
    };

    shapesMap.observeDeep(handleShapesChangeDeep);
    connectorsMap.observe(handleConnectorsChange);

    return () => {
      shapesMap.unobserveDeep(handleShapesChangeDeep);
      connectorsMap.unobserve(handleConnectorsChange);
    };
  }, [ydoc, isReady]);

  // Save function refs (stable across renders)
  const saveUpdatesRef = useRef<(() => Promise<void>) | null>(null);
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const lastFlushErrorRef = useRef<Error | null>(null);

  // Batch and send Yjs updates to server
  useEffect(() => {
    saveUpdatesRef.current = async () => {
      // If already saving, wait for it to complete - don't start a new save
      if (flushPromiseRef.current) {
        try {
          await flushPromiseRef.current;
        } catch {
          // Ignore errors, they're handled in the promise
        }
        // After the previous save completes, check if there are new updates to save
        if (pendingUpdatesRef.current.length > 0 && !isSavingRef.current) {
          // Recursively call to save the new updates
          return saveUpdatesRef.current?.();
        }
        return;
      }

      if (pendingUpdatesRef.current.length === 0) {
        return;
      }

      if (!flushPromiseRef.current) {
        flushPromiseRef.current = (async () => {
          if (isSavingRef.current || pendingUpdatesRef.current.length === 0) {
            return;
          }

          // Wait a short moment to allow any pending updates from the same bulk operation to accumulate
          // This ensures all updates from a single bulk action are batched together
          await new Promise(resolve => setTimeout(resolve, 50));

          // Check again after waiting - if new updates came in, they're now included
          if (pendingUpdatesRef.current.length === 0) {
            return;
          }

          isSavingRef.current = true;

          // Log how many updates we're batching
          const updateCount = pendingUpdatesRef.current.length;
          console.log(`[DiagramTanstackLink] Batching ${updateCount} Yjs updates into single save request`);

          try {
            // Send the FULL document state (not incremental updates)
            // This ensures the server has the complete, correct state
            const fullStateUpdate = Y.encodeStateAsUpdate(ydoc);
            
            // Use different endpoints based on access mode
            const ingestUrl = isLinkMode 
              ? `/api/link/diagrams/${diagramId}/ingest`
              : `/api/diagrams/${diagramId}/ingest`;
            
            console.log(`[DiagramTanstackLink] Sending single save request to ${ingestUrl} (${fullStateUpdate.length} bytes)`);
            
            const response = isLinkMode 
              ? await fetch(ingestUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Document-Epoch': String(documentEpochRef.current),
                  },
                  body: fullStateUpdate as BodyInit,
                  credentials: 'include',
                  keepalive: true,
                })
              : await authenticatedFetch(ingestUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Document-Epoch': String(documentEpochRef.current),
                  },
                  body: fullStateUpdate as BodyInit,
                  keepalive: true,
                });

            if (!response.ok) {
              throw new Error(`Ingest failed: ${response.statusText}`);
            }

            console.log(`[DiagramTanstackLink] Save request completed successfully`);

            // Clear pending updates on success so subsequent saves only send diffs
            pendingUpdatesRef.current = [];
            lastFlushErrorRef.current = null;
          } catch (error) {
            console.error('[DiagramTanstackLink] Failed to save updates:', error);
            // Keep updates in buffer for retry and surface upstream requestors
            lastFlushErrorRef.current = error instanceof Error ? error : new Error(String(error));
          } finally {
            isSavingRef.current = false;
          }
        })().finally(() => {
          flushPromiseRef.current = null;
        });
      }

      try {
        await flushPromiseRef.current;
      } catch (_error) {
        // Errors are handled above; swallow to avoid unhandled rejections
      }
    };
  }, [diagramId, authenticatedFetch, ydoc, isLinkMode]);

  const flushPendingUpdates = useCallback(async () => {
    // Chase any queued save that the debouncer scheduled
    if (saveUpdatesRef.current) {
      await saveUpdatesRef.current();
    }

    if (flushPromiseRef.current) {
      await flushPromiseRef.current;
    }

    if (lastFlushErrorRef.current) {
      const error = lastFlushErrorRef.current;
      lastFlushErrorRef.current = null;
      throw error;
    }
  }, []);

  // Listen to Yjs updates and batch them (attach immediately, not gated by isReady)
  useEffect(() => {
    const handleUpdate = (update: Uint8Array, origin: unknown) => {
      // Ignore updates from server (to avoid loops)
      if (origin === 'server') return;

      // Buffer update
      pendingUpdatesRef.current.push(update);
      lastUpdateTimeRef.current = Date.now();

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Don't save immediately - always use debouncing to batch all updates from a single operation
      // This ensures that even if multiple Yjs transactions fire quickly (e.g., from CompositeCommand),
      // they all get batched into a single HTTP request
      const scheduleSave = () => {
        // If already saving, don't schedule another save - it will be handled after current save completes
        if (isSavingRef.current) {
          return;
        }

        // Wait 200ms after the last update to ensure all updates from the same bulk operation are included
        // Increased timeout to better handle rapid sequential transactions
        saveTimeoutRef.current = setTimeout(() => {
          // Double-check that no new updates came in during the wait and we're not already saving
          const timeSinceLastUpdate = Date.now() - lastUpdateTimeRef.current;
          if (isSavingRef.current) {
            // Another save is in progress, don't start a new one
            return;
          }
          if (timeSinceLastUpdate < 100) {
            // New update came in recently, wait a bit more
            scheduleSave();
          } else {
            // No new updates and not saving, safe to save
            saveTimeoutRef.current = null;
            if (pendingUpdatesRef.current.length > 0) {
              saveUpdatesRef.current?.();
            }
          }
        }, 200);
      };

      scheduleSave();
    };

    ydoc.on('update', handleUpdate);

    return () => {
      ydoc.off('update', handleUpdate);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [ydoc]);

  // Save before page unload (synchronous to ensure reliability)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // CRITICAL: Must be synchronous to work on refresh/close
      // Even if a timeout is pending, force immediate save
      if (pendingUpdatesRef.current.length === 0 && !isSavingRef.current) {
        return; // Nothing to save
      }
      
      // Get the full document state synchronously
      const fullStateUpdate = Y.encodeStateAsUpdate(ydoc);
      
      const token = getCookie('gravity_token') ?? undefined;
      
      // Use different endpoints based on access mode
      const url = isLinkMode 
        ? `/api/link/diagrams/${diagramId}/ingest`
        : `/api/diagrams/${diagramId}/ingest`;
      
      // Try sendBeacon first (most reliable for unload scenarios)
      if (navigator.sendBeacon) {
        // sendBeacon requires a Blob for binary data
        const blob = new Blob([fullStateUpdate as BlobPart], { type: 'application/octet-stream' });
        
        // Create a URL with auth in query param as fallback (since sendBeacon can't set headers)
        const query = new URLSearchParams({ documentEpoch: String(documentEpochRef.current) });
        if (token) query.set('token', token);
        const beaconUrl = `${url}?${query.toString()}`;
        
        const sent = navigator.sendBeacon(beaconUrl, blob);
        
        if (sent) {
          // Clear pending updates since sendBeacon succeeded
          pendingUpdatesRef.current = [];
          return;
        }
      }
      
      // Fallback: synchronous fetch with keepalive
      // This initiates the request synchronously, browser keeps it alive
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/octet-stream',
          'X-Document-Epoch': String(documentEpochRef.current),
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        fetch(url, {
          method: 'POST',
          headers,
          body: fullStateUpdate as BodyInit,
          keepalive: true,
        }).catch(err => {
          console.error('[DiagramTanstackLink] beforeunload fetch failed:', err);
        });
        
        // Mark as saved
        pendingUpdatesRef.current = [];
      } catch (err) {
        console.error('[DiagramTanstackLink] beforeunload save failed:', err);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final save on unmount (still try async approach here)
      if (pendingUpdatesRef.current.length > 0) {
        saveUpdatesRef.current?.();
      }
    };
  }, [diagramId, ydoc, isLinkMode]);

  // Electric polling for sync (recovery from server changes)
  useEffect(() => {
    if (!isReady) return;

    let cancelled = false;
    let lastSnapshot: string | null = null;

    async function pollElectric() {
      if (cancelled) return;

      // Don't poll while saving to avoid conflicts
      if (isSavingRef.current || pendingUpdatesRef.current.length > 0) {
        return;
      }

      try {
        // Use different endpoints based on access mode
        const snapshotUrl = isLinkMode 
          ? `/api/link/diagrams/${diagramId}/snapshot`
          : `/api/diagrams/${diagramId}/snapshot`;
        
        const response = isLinkMode 
          ? await fetch(snapshotUrl, { credentials: 'include' })
          : await authenticatedFetch(snapshotUrl);
        
        if (!response.ok) return;

        const data = await response.json();
        
        if (data.snapshotBytesBase64 && data.snapshotBytesBase64 !== lastSnapshot) {
          lastSnapshot = data.snapshotBytesBase64;
          
          // Apply server updates (CRDT will merge with local state)
          const serverSnapshot = base64ToUint8Array(data.snapshotBytesBase64);
          Y.applyUpdate(ydoc, serverSnapshot, 'server');
        }
      } catch (error) {
        Logger.warn('DiagramTanstackLink', 'Electric poll failed:', error);
      }
    }

    // Poll every 5 seconds
    const interval = setInterval(pollElectric, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [diagramId, ydoc, isReady, authenticatedFetch, isLinkMode]);

  return {
    ydoc,
    awareness,
    isConnected,
    isReady,
    error,
    flushPendingUpdates,
  };
}
